/**
 * Накопление и метрики потока ошибок.
 *
 * Подневные «нашли» и «исправили» сами по себе — шум: в них видно, что вчера
 * было 40, а сегодня 12, и не видно ничего из того, ради чего читатель открыл
 * отчёт. Накопительные кривые превращают тот же ряд в два ответа: насколько
 * команда отстаёт (расстояние между кривыми по вертикали) и сколько ошибка
 * ждёт исправления (расстояние по горизонтали).
 *
 * @format
 */

/**
 * Один день ряда.
 *
 * Поля названы как в источнике: `new` — сколько ошибок система нашла в этот
 * день, `fixed` — сколько закрыто. Пользователю они показываются как
 * «найденные» и «исправленные»: слово «новые» в отчёте директору читается как
 * «свежая партия проблем», хотя это ровно те же ошибки, что и вчерашние.
 */
export interface IncidentDay {
  /**
   * Календарная дата, ISO `ГГГГ-ММ-ДД` — как приходит с сервера.
   *
   * Именно ISO, а не готовая подпись: формат представления накладывается в
   * момент показа. Иначе дата дважды меняет форму — сначала в человеческий
   * вид на входе, потом обратно разбором строки на оси, — и место, где её
   * можно сравнить или посчитать, теряется по дороге.
   */
  date: string;
  new: number;
  fixed: number;
}

export interface IncidentFlowPoint {
  /** Та же ISO-дата, что во входном дне. */
  date: string;
  /** Найдено накопительно к концу дня. */
  found: number;
  /** Исправлено накопительно к концу дня. */
  fixed: number;
  /** Разрыв между кривыми: сколько ошибок ждёт исправления. */
  open: number;
  /**
   * Исправлено ЗА ЭТОТ день, не накопительно.
   *
   * Накопительная кривая отвечает на «сколько всего», но не на «работали ли
   * сегодня»: у неё пологий участок и рывок выглядят одинаково, пока не
   * приложить линейку к соседним точкам.
   */
  fixedToday: number;
  /**
   * Та же величина долей от того, что ждало исправления на начало дня.
   *
   * Без знаменателя «исправлено 40» не читается: сорок из пятидесяти — это
   * разобранный завал, сорок из тысячи — статистическая рябь.
   */
  fixedTodayShare: number;
}

export interface IncidentFlowMetrics {
  points: IncidentFlowPoint[];
  /** Найдено ЗА ПЕРИОД — без долга, пришедшего из прошлого. */
  totalFound: number;
  totalFixed: number;
  /** Открыто на конец периода. */
  openAtEnd: number;
  /**
   * Сколько из старого долга закрыто за период.
   *
   * Считается по правилу «сначала берутся самые старые»: это не факт из
   * данных, а допущение — подневный ряд не говорит, какую именно ошибку
   * закрыли. Допущение выбрано так, чтобы метрика не льстила: при обратном
   * порядке старый долг вечно оставался бы нетронутым.
   */
  backlogClosed: number;
  /**
   * Среднее время от находки до исправления, в днях.
   *
   * Считается горизонтальным зазором: для уровня, до которого кривая
   * найденного дошла в день i, ищем день, когда до того же уровня добралась
   * кривая исправленного. `null`, если исправлено слишком мало и зазор не
   * замыкается ни на одном уровне.
   */
  avgLagDays: number | null;
  /** Максимум одновременно открытых за период — пик долга. */
  peakOpen: number;
}

/**
 * Накопительные суммы по дням.
 *
 * `openingBacklog` — сколько ошибок было открыто НА НАЧАЛО периода. Без него
 * график ломается на первом же месяце, где команда разбирает старые завалы:
 * закрытий выходит больше, чем находок, и кривая исправленного оказывается
 * выше кривой найденного — картина, невозможная по смыслу (нельзя исправить
 * то, что не находили). Долг, пришедший из прошлого периода, — это тоже
 * найденные ошибки, просто найденные раньше, поэтому он и входит в верхнюю
 * кривую с самого начала.
 */
export function toFlowPoints(days: IncidentDay[], openingBacklog = 0): IncidentFlowPoint[] {
  let found = openingBacklog;
  let fixed = 0;
  return days.map(day => {
    found += day.new;
    const before = fixed;
    fixed += day.fixed;
    // Сколько день закрыл на самом деле: ниже кривая упирается в потолок
    // `found`, и разница «до/после» — единственная честная дневная величина.
    const closedToday = Math.min(fixed, found) - before;
    // Клампим: закрытий больше, чем всего найденного, не бывает — такие данные
    // означают заниженный `openingBacklog`, и честнее упереть кривую в потолок,
    // чем нарисовать отрицательную очередь.
    fixed = Math.min(fixed, found);
    // Долг на НАЧАЛО дня — знаменатель дневной доли. Берём его до того,
    // как день закроет свою часть, иначе доля считалась бы от остатка и в
    // день полного разбора завала дала бы деление на ноль.
    const openBefore = found - day.new - (fixed - closedToday);
    const fixedTodayShare = openBefore > 0 ? (closedToday / openBefore) * 100 : 0;
    return {date: day.date, found, fixed, open: found - fixed, fixedToday: closedToday, fixedTodayShare};
  });
}

/**
 * Средний горизонтальный зазор, взвешенный по числу находок дня.
 *
 * Обе кривые монотонны, поэтому день-догонялку ищем одним указателем, который
 * не откатывается назад: для более высокого уровня он не может оказаться
 * раньше. Дни, чей уровень исправленное так и не догнало, в среднее не входят
 * — это не «бесконечно долгий ремонт», а незакрытый остаток, и он показан
 * отдельно как `openAtEnd`.
 */
function averageLag(points: IncidentFlowPoint[], days: IncidentDay[]): number | null {
  let weighted = 0;
  let weight = 0;
  let j = 0;

  points.forEach((point, i) => {
    const added = days[i].new;
    if (added <= 0) return;
    if (j < i) j = i;
    while (j < points.length && points[j].fixed < point.found) j += 1;
    if (j >= points.length) return;
    weighted += added * (j - i);
    weight += added;
  });

  return weight > 0 ? weighted / weight : null;
}

/**
 * Индексы `count` самых высоких (или самых низких) значений, разнесённых
 * минимум на `minGap` позиций.
 *
 * Без разнесения обе подписи садятся на один горб — вершину и её соседа, —
 * и вторая не сообщает ничего сверх первой, зато перекрывает её собой.
 * Разнесение заставляет вторую подпись найти ДРУГОЙ горб.
 */
export function pickExtremes(
  values: number[],
  mode: 'max' | 'min',
  count: number,
  minGap: number,
  /** Значения, уже подписанные где-то ещё: повторять их незачем. */
  taken: Iterable<number> = [],
): number[] {
  const order = values
    .map((value, index) => ({value, index}))
    .sort((a, b) => (mode === 'max' ? b.value - a.value : a.value - b.value));

  // Две подписи с одним и тем же числом ничего не добавляют ко второй, зато
  // читаются как ошибка: «почему тут дважды 0?». Одно значение — одна подпись.
  const seen = new Set<number>(taken);
  const picked: number[] = [];
  for (const {value, index} of order) {
    if (picked.length >= count) break;
    if (seen.has(value)) continue;
    if (picked.some(other => Math.abs(other - index) < minGap)) continue;
    seen.add(value);
    picked.push(index);
  }
  return picked.sort((a, b) => a - b);
}

export function incidentFlowMetrics(days: IncidentDay[], openingBacklog = 0): IncidentFlowMetrics {
  const points = toFlowPoints(days, openingBacklog);
  // Явная проверка длины, а не `points.at(-1)?.found ?? 0`: типы считают
  // элемент массива непустым, и `eslint --fix` вычищает такое `?.` как
  // «лишнее» — на пустом ряде это падало бы в рантайме.
  const last = points.length > 0 ? points[points.length - 1] : null;
  // Из итога вычитаем стартовый долг: «найдено за период» — это находки
  // периода, а не всё, что когда-либо висело открытым.
  const totalFound = last ? last.found - openingBacklog : 0;
  const totalFixed = last ? last.fixed : 0;

  return {
    points,
    totalFound,
    totalFixed,
    // Очередь берётся с графика, а не пересчитывается из итогов: на графике
    // она уже учла стартовый долг, и два разных ответа на один вопрос —
    // верный способ однажды разойтись.
    openAtEnd: last ? last.open : 0,
    backlogClosed: Math.min(openingBacklog, totalFixed),
    avgLagDays: averageLag(points, days),
    peakOpen: points.reduce((max, p) => Math.max(max, p.open), 0),
  };
}
